import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  User, 
  LogOut, 
  Calendar,
  ChevronLeft,
  Menu,
  X,
  Phone,
  Settings
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import SIPDialer from '@/components/SIPDialer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL for logo
const getLogoUrl = (url) => {
  if (!url) return '';
  // Already a full URL (R2 or external)
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  // Local API path - prepend backend URL
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  // Other local path - prepend backend URL with /api
  if (url.startsWith('/')) return `${BACKEND_URL}/api${url}`;
  // Relative path without leading slash
  return `${BACKEND_URL}/api/uploads/${url}`;
};

const PortalHeader = ({ portalSettings, showBackButton = false, backPath = '/' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [guest, setGuest] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check for logged-in guest
    const guestData = localStorage.getItem('portal_guest');
    if (guestData) {
      try {
        setGuest(JSON.parse(guestData));
      } catch (e) {
        console.error('Failed to parse guest data:', e);
      }
    }
  }, [location.pathname]); // Re-check on route change

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_guest');
    setGuest(null);
    navigate('/');
  };

  const handleLogin = () => {
    const currentPath = location.pathname + location.search;
    navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
  };

  return (
    <header 
      className="py-4 px-4 shadow-sm sticky top-0 z-50"
      style={{ backgroundColor: portalSettings?.header_bg_color || portalSettings?.secondary_color || '#2d3748' }}
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        {/* Left Side - Logo/Back */}
        <div className="flex items-center gap-4">
          {showBackButton && (
            <button 
              onClick={() => navigate(backPath)}
              className="hover:opacity-70 p-1"
              style={{ color: portalSettings?.header_text_color || '#ffffff' }}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 font-semibold hover:opacity-80 text-lg"
            style={{ color: portalSettings?.header_text_color || '#ffffff' }}
          >
            {portalSettings?.portal_logo ? (
              <img 
                src={getLogoUrl(portalSettings.portal_logo)} 
                alt={portalSettings?.portal_name || 'Logo'}
                className="h-8 w-auto max-w-[140px]"
                style={{ objectFit: 'contain', objectPosition: 'left center' }}
                onError={(e) => {
                  // If logo fails to load, show name instead
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline';
                }}
              />
            ) : null}
            <span style={{ display: portalSettings?.portal_logo ? 'none' : 'inline' }}>
              {portalSettings?.portal_name || 'Book Your Stay'}
            </span>
          </button>
        </div>

        {/* Right Side - Auth */}
        <div className="flex items-center gap-3">
          {guest ? (
            <>
              {/* My Bookings Button */}
              <Button
                variant="ghost"
                size="sm"
                className="hover:bg-white/10 hidden sm:flex"
                style={{ color: portalSettings?.header_text_color || '#ffffff' }}
                onClick={() => navigate('/my-bookings')}
              >
                <Calendar className="h-4 w-4 mr-2" />
                My Bookings
              </Button>

              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-white/30 hover:bg-white/10"
                    style={{ color: portalSettings?.header_text_color || '#ffffff' }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">{guest.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="font-medium text-sm">{guest.name}</p>
                    <p className="text-xs text-gray-500">{guest.phone}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/my-bookings')}>
                    <Calendar className="h-4 w-4 mr-2" />
                    My Bookings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="h-4 w-4 mr-2" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="hover:bg-white/10"
                style={{ color: portalSettings?.header_text_color || '#ffffff' }}
                onClick={handleLogin}
                data-testid="portal-login-btn"
              >
                Log In
              </Button>
              <Button
                size="sm"
                className="text-white hover:opacity-90"
                style={{ backgroundColor: portalSettings?.primary_color || '#f97316' }}
                onClick={() => navigate(`/signup?returnUrl=${encodeURIComponent(location.pathname + location.search)}`)}
                data-testid="portal-signup-btn"
              >
                Sign Up
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default PortalHeader;

// Helper function to check if user is logged in
export const isPortalLoggedIn = () => {
  const token = localStorage.getItem('portal_token');
  const guest = localStorage.getItem('portal_guest');
  return !!(token && guest);
};

// Helper function to get logged-in guest
export const getPortalGuest = () => {
  const guestData = localStorage.getItem('portal_guest');
  if (guestData) {
    try {
      return JSON.parse(guestData);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Helper function to get portal token
export const getPortalToken = () => {
  return localStorage.getItem('portal_token');
};

// Helper function to require auth and redirect if not logged in
export const requirePortalAuth = (navigate, currentPath) => {
  if (!isPortalLoggedIn()) {
    navigate(`/login?returnUrl=${encodeURIComponent(currentPath)}`);
    return false;
  }
  return true;
};
